import { useEffect } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorPageProps {
    /** The error that was caught */
    error?: Error | null;
    /** Callback to retry/reset the error state */
    onRetry?: () => void;
}

/**
 * Error Page Component
 *
 * Displayed when an unhandled error occurs in the application.
 * Shows a user-friendly message with options to retry or navigate home.
 */
export const ErrorPage = ({ error, onRetry }: ErrorPageProps) => {
    const isDevelopment = import.meta.env.DEV;

    // ErrorPage is rendered by the client-side ErrorBoundary, not by a route,
    // so there's no `meta` export to drive head tags. Set the document title +
    // robots directive directly while the boundary is open.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const previousTitle = document.title;
        document.title = "We couldn't load this page";
        let robotsTag = document.querySelector<HTMLMetaElement>('meta[name="robots"][data-error-page]');
        if (!robotsTag) {
            robotsTag = document.createElement('meta');
            robotsTag.name = 'robots';
            robotsTag.dataset.errorPage = 'true';
            robotsTag.content = 'noindex, nofollow';
            document.head.appendChild(robotsTag);
        }
        return () => {
            document.title = previousTitle;
            robotsTag?.remove();
        };
    }, []);

    return (
        <>
            <div className="min-h-screen flex items-center justify-center bg-muted px-4">
                <div className="text-center max-w-md">
                    {/* Error Icon */}
                    <div className="mx-auto w-16 h-16 bg-destructive-subtle rounded-full flex items-center justify-center mb-6">
                        <AlertTriangle className="w-8 h-8 text-destructive" aria-hidden="true" />
                    </div>

                    {/* Error Message */}
                    <h1 className="text-2xl font-bold text-foreground mb-2">
                        We couldn&apos;t load this page
                    </h1>

                    <p className="text-muted-foreground mb-6">
                        Something unexpected happened while loading this page.
                        Try again, or head back to the homepage.
                    </p>

                    {/* Error Details (Development Only) */}
                    {isDevelopment && error && (
                        <details className="mb-6 text-left bg-muted rounded-lg p-4">
                            <summary className="cursor-pointer font-medium text-foreground select-none">
                                Error Details (Development Only)
                            </summary>
                            <div className="mt-3 space-y-2">
                                <p className="text-sm font-mono text-destructive break-words">
                                    {error.message}
                                </p>
                                {error.stack && (
                                    <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-48 bg-muted p-2 rounded">
                                        {error.stack}
                                    </pre>
                                )}
                            </div>
                        </details>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        {onRetry && (
                            <Button
                                onClick={onRetry}
                                className="gap-2"
                                variant="default"
                            >
                                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                                Try loading again
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            asChild
                            className="gap-2"
                        >
                            <Link to="/">
                                <Home className="w-4 h-4" aria-hidden="true" />
                                Go to homepage
                            </Link>
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ErrorPage;
